// @flow
// Full flow mode was easier than weak mode - full mode helped resolve _.findIndex(...) below

import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';

import fuzzyscore from 'app/common/fuzzyscore';
import { toLowerCaseString } from 'app/common/CommonUtils';

import type { ComponentType } from 'react';

// Score a `choice` based on the type of `choice` it is
// and how it compares to `searchString`, where a higher
// score means it should sort later than a lower score.
const scoreChoice = (choice, searchString) => {
  // Ensure path headings are at the beginning
  const pathChoiceScore = choice.isPathHeader ? -Infinity : 0;
  // Ensure section headings are at the beginning
  const sectionChoiceScore = choice.sectionKey ? -9999 : 0;
  // Ensure "special" choices are at the end
  const specialChoiceScore = choice.action ? Infinity : 0;
  // Then sort by fuzzyscore, prioritizing exact matches
  const fuzzyScore = -(
    fuzzyscore(searchString, choice.label) +
    fuzzyscore(searchString, choice.sample)
  );

  return pathChoiceScore + specialChoiceScore + sectionChoiceScore + fuzzyScore;
};

const sortChoicesOfGroup = (choices, searchString) => {
  return _.sortBy(choices, [
    // Higher scores sort after lower scores
    choice => scoreChoice(choice, searchString),
    // Alphabetize to tiebreak
    choice => toLowerCaseString(choice.label),
    choice => toLowerCaseString(choice.sample),
  ]);
};

// Sort a flat array of `choices` by grouping them by
// their `groupId` and sorting each group's choices.
export const groupAndSortChoices = (
  choices: { shouldShowAtEnd: boolean }[],
  searchString: string
) => {
  // keep special sections like 'Other Options' at the end, and don't mess with their ordering
  const showAtEndIndex = _.findIndex(choices, c => c.shouldShowAtEnd);
  const splitIndex = showAtEndIndex === -1 ? choices.length : showAtEndIndex;
  const choicesToSort = choices.slice(0, splitIndex);
  const specialChoices = choices.slice(splitIndex);

  const sortedChoices = _.chain(choicesToSort)
    .groupBy('groupId')
    .reduce((groups, groupChoices, stepNumber) => {
      groups[stepNumber] = sortChoicesOfGroup(groupChoices, searchString);
      return groups;
    }, {})
    .values()
    .flatten()
    .value();

  return sortedChoices.concat(specialChoices);
};

export const ChoicesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })
);

export const wrapWithFormaticField = (
  Component: ComponentType<*>,
  props: { [key: string]: any }
) => {
  return props.config.createElement(
    'field',
    {
      config: props.config,
      field: props.field,
      key: props.field.key,
    },
    <Component {...props} />
  );
};
